import { useState, useEffect, Suspense, type FC } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { StateValidator } from "../components/StateValidator";
import { YearSwitchBanner } from "../components/YearSwitchBanner";
import { GlobalSessionHandler } from "../components/GlobalSessionHandler";
import { ScrollRestoration } from "../components/ScrollRestoration";
import { PrivateRoute } from "../components/PrivateRoute";
import { ReferrerDetector } from "../components/ReferrerDetector";
import { Footer } from "../components/Footer";
import { LoadingMask } from "../components/LoadingMask";
import { InjectIntlContext } from "../helpers/intlHooks";
import { useYearSwitchBanner } from "../helpers/yearHelpers";
import * as Pages from "../pages";
import messagesEn from "../translations/en-US.json";
import messagesEs from "../translations/es-MX.json";
import { routes } from "./routes";
import { RouteToastHandler } from "../components/RouteToastHandler";
import { IdleTimeout } from "../components/IdleTimeout/IdleTimeout";
import { MaxGlobalHeader } from "./MaxGlobalHeader";
import { useYearPart } from "../helpers/yearFlagHelpers";
import { AnalyticsListeners } from "../components/AnalyticsListeners";
import { IntlProvider } from "react-intl";
import { shouldPolyfill as shouldPolyfillRelativeTimeFormat } from "@formatjs/intl-relativetimeformat/should-polyfill";
import { shouldPolyfill as shouldPolyfillPluralRules } from "@formatjs/intl-pluralrules/should-polyfill";
import { AnonRoute } from "../components/AnonRoute";
import { getLanguageFromLocationOrState } from "../helpers/languageHelpers";
import { useAppContext } from "../helpers/context-hooks/useAppContext";
import { useBackendVersions } from "../helpers/query-hooks/useBackendVersions";
import { useMctFlags } from "../helpers/flag-hooks/useMctFlags";
import { useLoggedIn } from "../helpers/loginHelpers";
import { useSearchParams } from "../helpers/routing-hooks/useSearchParams";
import { isFromMedigap } from "../helpers/routeHelpers";
/**
 * For Safari & Mobile Safari, add a `scrollend` event listener polyfill
 * @TODO - remove this line and the dependency if Apple ever implements this spec
 */
import "scrollyfills";
import { MCT_APP_MAIN_ID } from "../config";
import { DrawerManager } from "@cmsgov/design-system-12";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { getLdUser } from "../helpers/launchDarklyHelpers";

import { ProviderNetworkRoute } from "../components/ProviderNetwork/ProviderNetworkRoute";

export const AppContent: FC = () => {
  // * Context
  const { state } = useAppContext();

  // * Queries
  // this prefetches the backend version numbers.
  const { hasAttempted } = useBackendVersions();

  // * Hooks
  const location = useLocation();
  const showYearSwitchBanner = useYearSwitchBanner();
  const isLoggedIn = useLoggedIn();
  const { selectedLanguage, selectedYear } = useSearchParams(true);
  const ldClient = useLDClient();

  // * Language
  const language = getLanguageFromLocationOrState({
    location,
    stateLang: state.language,
  });

  // For conditional render of the landing page route
  const url = window.location.href.split("/?")[0];
  const fromMedigap = isFromMedigap(url);

  // The IntlProvider adds language localization to its children with a default language and translations map as props
  const messages = {
    en: messagesEn,
    es: messagesEs,
  };

  useEffect(() => {
    (async () => {
      await polyfill("en");
      await polyfill("es");
    })();
  }, []);

  // * Flags
  const flags = useMctFlags();
  const {
    feMaintenanceMode,
    tmpFeEnableProviderNetwork,
    tmpFeEnableManagingProviders,
  } = flags;
  const { isOutsideOpenEnrollment } = useYearPart();

  // * State
  const [ldFlagTimeout, setLdFlagTimeout] = useState<
    NodeJS.Timeout | undefined
  >();
  const [couldNotRetrieveLdFlags, setCouldNotRetrieveLdFlags] = useState(false);
  const [ldFlagsAvailable, setLdFlagsAvailable] = useState(false);

  // * Constants
  const isLoadingFlags = !(ldFlagsAvailable || couldNotRetrieveLdFlags);
  const isLoadingForTheFirstTime = isLoadingFlags || !hasAttempted;

  // * Effects
  useEffect(() => {
    const ldFlagsAreAvailable = Object.getOwnPropertyNames(flags).length > 0;

    setLdFlagsAvailable(ldFlagsAreAvailable);

    if (ldFlagsAreAvailable) {
      if (ldFlagTimeout) {
        clearTimeout(ldFlagTimeout);
        setLdFlagTimeout(undefined);
      }
      setCouldNotRetrieveLdFlags(false);
    } else {
      setLdFlagTimeout(
        setTimeout(() => {
          setCouldNotRetrieveLdFlags(true);
        }, 5000)
      );
    }
    // ! TODO: See MCT-11398
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This error has been grandfathered in and needs to be fixed.
  }, [flags]);

  useEffect(() => {
    ldClient?.identify(getLdUser(state.beneficiary));
  }, [state.beneficiary, ldClient]);

  return (
    <IntlProvider locale={language} messages={messages[language]}>
      <InjectIntlContext>
        <MaxGlobalHeader>
          <StateValidator>
            <ScrollRestoration>
              <ReferrerDetector>
                <GlobalSessionHandler>
                  <IdleTimeout>
                    <AnalyticsListeners>
                      <RouteToastHandler>
                        <div className="ds-base mct-l-app">
                          {showYearSwitchBanner && <YearSwitchBanner />}
                          <Suspense
                            fallback={
                              <LoadingMask
                                loading={true}
                                message={
                                  messages[state.language]["app.loading"]
                                }
                              />
                            }
                          >
                            <DrawerManager>
                              {isLoadingForTheFirstTime ? (
                                <Pages.LoadingPage />
                              ) : (
                                <div className="AppContent mct-l-app__content">
                                  <main
                                    id={`${MCT_APP_MAIN_ID}`}
                                    className="app__main mct-l-app__main e2e-app-main"
                                    tabIndex={-1}
                                  >
                                    {feMaintenanceMode ||
                                    couldNotRetrieveLdFlags ? (
                                      <Pages.MaintenancePage />
                                    ) : (
                                      <Switch>
                                        <Route
                                          path={routes.searchResults}
                                          component={Pages.SearchResultsPage}
                                        />
                                        <Route
                                          path={routes.sanctionedPlans}
                                          component={Pages.SanctionedPlansPage}
                                        />
                                        <Route
                                          path={routes.drugSearchPreferences}
                                          component={
                                            Pages.DrugSearchPreferencesPage
                                          }
                                        />
                                        {!isOutsideOpenEnrollment && (
                                          <Route
                                            path={routes.yearOverYear}
                                            component={Pages.PlanComparePage}
                                          />
                                        )}
                                        <Route
                                          path={routes.comparePlans}
                                          component={Pages.PlanComparePage}
                                        />
                                        <Route
                                          path={routes.planDetails}
                                          component={Pages.PlanDetailsPage}
                                        />
                                        <Route
                                          path={routes.managePrescriptions}
                                          component={
                                            Pages.ManagePrescriptionsPage
                                          }
                                        />
                                        <Route
                                          path={routes.prescriptionsList}
                                          component={
                                            Pages.ManagePrescriptionsPage
                                          }
                                        />
                                        <Route
                                          path={routes.managePharmacies}
                                          component={Pages.ManagePharmaciesPage}
                                        />
                                        <Route
                                          path={routes.inNetworkPharmacy}
                                          component={
                                            Pages.InNetworkPharmaciesPage
                                          }
                                        />
                                        <Route
                                          path={routes.pharmacy}
                                          component={Pages.SelectPharmaciesPage}
                                        />
                                        <Route
                                          path={routes.questionRouting}
                                          component={Pages.QuestionRoutingPage}
                                        />
                                        <Route
                                          path={routes.lisQuestions}
                                          component={Pages.LisQuestionsPage}
                                        />
                                        <Route
                                          path={routes.enroll}
                                          component={Pages.EnrollmentFormPage}
                                        />
                                        <Route
                                          path={routes.csrLanding}
                                          component={Pages.CSRLandingPage}
                                        />
                                        <Route
                                          path={routes.pdePage}
                                          component={Pages.PDEPage}
                                        />
                                        <Route
                                          path={routes.version}
                                          component={Pages.VersionPage}
                                        />
                                        <Route
                                          path={routes.planPreview}
                                          component={Pages.PlanPreviewPage}
                                        />
                                        <Route
                                          path={routes.coverageWizard.options}
                                          component={
                                            Pages.CoverageWizardOptionsPage
                                          }
                                        />
                                        <Route
                                          path={routes.coverageWizard.info}
                                          component={
                                            Pages.CoverageWizardInfoPage
                                          }
                                        />
                                        <Route
                                          path={
                                            routes.coverageWizard.landingPage
                                          }
                                          component={
                                            Pages.CoverageWizardLandingPage
                                          }
                                        />
                                        <Route
                                          path={routes.pace.plans}
                                          component={Pages.PacePlansPage}
                                        />
                                        <Route
                                          path={routes.pace.landingPage}
                                          component={Pages.PaceLandingPage}
                                        />
                                        <Route
                                          path={routes.pap.plans}
                                          component={Pages.PapPlansPage}
                                        />
                                        <Route
                                          path={routes.spap.plans}
                                          component={Pages.SpapPlansPage}
                                        />
                                        <Route
                                          path={routes.spap.landingPage}
                                          component={Pages.SpapLandingPage}
                                        />
                                        <PrivateRoute
                                          path={routes.summary.lisQuestions}
                                        >
                                          <Pages.LisQuestionsPage
                                            isFromSummaryPage={true}
                                          />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          path={routes.summary.managePharmacies}
                                        >
                                          <Pages.ManagePharmaciesPage
                                            isFromSummaryPage={true}
                                          />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          path={
                                            routes.summary.managePrescriptions
                                          }
                                        >
                                          <Pages.ManagePrescriptionsPage
                                            isFromSummaryPage={true}
                                          />
                                        </PrivateRoute>

                                        <PrivateRoute
                                          path={routes.summary.selectPharmacies}
                                        >
                                          <Pages.SelectPharmaciesPage
                                            isFromSummaryPage={true}
                                          />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          path={
                                            routes.summary.drugSearchPreferences
                                          }
                                        >
                                          <Pages.DrugSearchPreferencesPage
                                            isFromSummaryPage={true}
                                          />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          path={routes.summary.pdePage}
                                        >
                                          <Pages.PDEPage
                                            isFromSummaryPage={true}
                                          />
                                        </PrivateRoute>
                                        <PrivateRoute
                                          path={routes.summary.searchResults}
                                        >
                                          <Pages.SearchResultsPage />
                                        </PrivateRoute>
                                        {tmpFeEnableProviderNetwork &&
                                          tmpFeEnableManagingProviders && (
                                            <PrivateRoute
                                              path={
                                                routes.summary.manageProviders
                                              }
                                            >
                                              <Pages.ManageProvidersPage
                                                isFromSummaryPage={true}
                                              />
                                            </PrivateRoute>
                                          )}
                                        {/* All private routes need to be added 
                                        BEFORE summary landing page. */}
                                        <PrivateRoute
                                          path={routes.summary.landingPage}
                                        >
                                          <Pages.SummaryPage />
                                        </PrivateRoute>
                                        <Route
                                          path={routes.pap.landingPage}
                                          component={Pages.PapLandingPage}
                                        />
                                        <Route
                                          path={routes.logout}
                                          component={Pages.LogoutPage}
                                        />
                                        <Route
                                          path={routes.medigap.plans}
                                          component={Pages.MedigapPlans}
                                        />
                                        <Route
                                          path={routes.medigap.planDetails}
                                          component={
                                            Pages.MedigapPlanDetailsPage
                                          }
                                        />
                                        <Route
                                          path={routes.medigap.policies}
                                          component={Pages.MedigapPoliciesPage}
                                        />
                                        <Route
                                          path={routes.medigap.landingPage}
                                          component={Pages.MedigapLandingPage}
                                        />

                                        <AnonRoute
                                          path={routes.newToMedicare}
                                          component={Pages.NewToMedicarePage}
                                        />

                                        <Route
                                          path={routes.findPlansNow}
                                          component={Pages.FindPlansNowPage}
                                        />

                                        <Route
                                          path={routes.csrGuestAccess}
                                          component={Pages.CSRGuestAccessPage}
                                        />

                                        <ProviderNetworkRoute
                                          path={
                                            routes.providerNetwork.addProviders
                                          }
                                          component={Pages.AddProvidersPage}
                                        />
                                        {tmpFeEnableManagingProviders && (
                                          <ProviderNetworkRoute
                                            path={
                                              routes.providerNetwork
                                                .manageProviders
                                            }
                                            component={
                                              Pages.ManageProvidersPage
                                            }
                                          />
                                        )}
                                        {/* Landing must be last, it's the root path */}
                                        <Route
                                          // Authenticated? Go to auth landing page
                                          render={routeProps =>
                                            // RE: Medigap exception
                                            // @see https://github.com/CMSgov/oc-mct-frontend/pull/2293
                                            isLoggedIn && !fromMedigap ? (
                                              <Redirect
                                                to={{
                                                  pathname:
                                                    routes.summary.landingPage,
                                                  search: `?year=${selectedYear}&lang=${selectedLanguage}`,
                                                  state: {
                                                    from: routeProps.location,
                                                  },
                                                }}
                                              />
                                            ) : (
                                              <Pages.LandingPage />
                                            )
                                          }
                                        />
                                      </Switch>
                                    )}
                                  </main>
                                  {!location.hash.includes(
                                    routes.planPreview.replace("/:id", "")
                                  ) && <Footer />}
                                </div>
                              )}
                            </DrawerManager>
                          </Suspense>
                        </div>
                      </RouteToastHandler>
                    </AnalyticsListeners>
                  </IdleTimeout>
                </GlobalSessionHandler>
              </ReferrerDetector>
            </ScrollRestoration>
          </StateValidator>
        </MaxGlobalHeader>
      </InjectIntlContext>
    </IntlProvider>
  );
};

async function polyfill(locale: string) {
  const unsupportedRelativeTimeLocale =
    shouldPolyfillRelativeTimeFormat(locale);
  const unsupportedPluralRulesLocale = shouldPolyfillPluralRules(locale);

  if (unsupportedRelativeTimeLocale) {
    // Load the polyfill 1st BEFORE loading data
    await import("@formatjs/intl-relativetimeformat/polyfill-force");
    await import(
      `@formatjs/intl-relativetimeformat/locale-data/${unsupportedRelativeTimeLocale}`
    );
  }

  if (unsupportedPluralRulesLocale) {
    // Load the polyfill 1st BEFORE loading data
    await import("@formatjs/intl-pluralrules/polyfill-force");
    await import(
      `@formatjs/intl-pluralrules/locale-data/${unsupportedPluralRulesLocale}`
    );
  }
}
